/*
================================================================================
File: src/pages/SupportPage.js
Description: Displays FAQs and links to support channels.
================================================================================
*/
import React, { useState } from "react";
import PageContent from "../components/common/PageContent";

const ContactCard = ({ icon, title, description, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md dark:hover:shadow-lg transition-shadow text-left flex flex-col items-start gap-3 group"
  >
    <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
        {title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {description}
      </p>
    </div>
  </button>
);

const SupportPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "I have a referral. What do I do?",
      answer:
        "Thanks for your faith and trust in us. You can register your referral here and we'll get them set up for the next REIA meeting: https://training.bigdogtools.com/referrals/ and if you want to forward them our website to get signed up, they can do that right now by going to www.TexasREIAS.com and selecting the city and the meeting they want to go to.",
    },
    {
      question: "I downloaded the app and can't log in?",
      answer:
        "Please check to make sure you're using the same information you initially provided and that you are using the same information provided in the welcome email. If that doesn't work, sometimes it may take a few minutes for the app servers to communicate and complete the account creation. Please wait 5 - 10 minutes and try again. If you still have troubles please email support@loveamericanhomes.com",
    },
    {
      question: "Can you send me the zoom link for the call tonight/today?",
      answer:
        "All zoom links for group Q&A calls can be found here: https://training.bigdogtools.com/join-a-call/. I put them in my personal calendar so I can just click on them easily. We also sends you a reminder 24 hours before each call and an hour before each call. Each day's call is the same week to week. Monday's link is always the same for Monday and so on. Also you can add the Big Dog calendar to your calendar by going to BigDogCalendar.com, scroll down to the Google Calenadr and press the + in the bottom right corner. It will then connect to your calendar as an additional calendar so you can grab the link each time. But if you want a pop-up reminder from your calendar you'll need to add it to your personal calendar.",
    },
    {
      question: "Is this person a Big Dog?",
      answer:
        "The best place for figuring out if someone is a Big Dog is by going to the Big Dog Facebook page and searching for their name in the membership area!",
    },
    {
      question: "What is the difference between a AI Query and a Skiptrace?",
      answer:
        "AI Query does a query on the home, and a Skiptrace does a query on a person(s).",
    },
  ];

  return (
    <PageContent title="Support" error={null}>
      <div className="p-8 space-y-8">
        {/* Hero Section */}
        <div className="bg-orange-500 rounded-xl p-8 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <svg
              className="w-8 h-8"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <h1 className="text-3xl font-bold">How can we help you?</h1>
          </div>
          <p className="text-orange-100 text-lg">
            Find answers in our FAQ below, or reach out to our team directly.
          </p>
        </div>

        {/* Quick Contact Cards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Contact & Resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ContactCard
              icon={
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              }
              title="Submit a Ticket"
              description="Create a support ticket for faster tracking"
              onClick={() =>
                window.open(
                  "https://snapflipbank.zendesk.com/hc/en-us/requests/new",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
            <ContactCard
              icon={
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              }
              title="Email Support"
              description="Reach out to our support team directly"
              onClick={() =>
                window.open(
                  "mailto:support@loveamericanhomes.com",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
            <ContactCard
              icon={
                <svg
                  className="w-6 h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              }
              title="Join a Call"
              description="Find live Q&A calls and zoom links"
              onClick={() =>
                window.open(
                  "https://training.bigdogtools.com/join-a-call/",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
            <ContactCard
              icon={
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.125-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.125-1.283.356-1.857m0 0a3.001 3.001 0 015.288 0M12 14a4 4 0 100-8 4 4 0 000 8z"
                  />
                </svg>
              }
              title="Community"
              description="Find Big Dog members on Facebook"
              onClick={() =>
                window.open(
                  "https://www.facebook.com/groups/thebigdogtoolsteam/",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <div key={index} className="rounded-lg overflow-hidden">
                <button
                  className={`flex justify-between items-center w-full text-left px-4 py-4 transition-colors ${
                    openFaq === index
                      ? "bg-orange-50 dark:bg-orange-900/20"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-orange-500 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0zM8 8a1 1 0 000 2h6a1 1 0 000-2H8zm0 3a1 1 0 000 2h3a1 1 0 000-2H8z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span
                      className={`font-semibold transition-colors ${
                        openFaq === index
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-gray-700 dark:text-gray-100"
                      }`}
                    >
                      {faq.question}
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform duration-200 flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-4 py-4 border-l-4 border-orange-400 bg-gray-50 dark:bg-gray-700/50">
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageContent>
  );
};

export default SupportPage;
